import { useStore } from '@tanstack/react-form';
import { Button, Dropdown, Input, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { SHOPIFY_PRICE_LISTS } from '../../../../constants/shopify';
import { useHandlers } from '../../../../context/page-context';
import { useTax } from '../../../../hooks/use-tax';
import { getCurrencySymbol } from '../../../../utils/format-money';
import { usePriceListForm } from '../../hooks/use-price-list-form';

function TaxConfiguration() {
  const { notificationHandler } = useHandlers();
  const { tax: initialTax } = useTax();
  const [tax, setTax] = useState<string>('');

  const form = usePriceListForm();

  const percentageVariation = useStore(
    form.store,
    (state) => state.values.automaticPricing?.percentageVariation,
  );

  const currency = useStore(form.store, (state) => state.values.currency);
  const currencySymbol = getCurrencySymbol(currency);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We don't want this to update when tax changes (if they empty the input, that would reset to the initialTax)
  useEffect(() => {
    if (initialTax?.country?.tax && tax === '') {
      setTax((initialTax.country.tax * 100).toPrecision(6));
    }
  }, [initialTax]);

  if (!percentageVariation) {
    return null;
  }

  function adjustPercentageTax() {
    if (!tax) {
      notificationHandler.error(t('price_list_builder.notifications.errors.tax_adjust'));
      return;
    }

    const parsedTax = Number(tax);
    const parsedAdjustPercent = Number(percentageVariation?.amount);
    if (Number.isNaN(parsedTax) || Number.isNaN(parsedAdjustPercent)) {
      notificationHandler.error(t('price_list_builder.notifications.errors.tax_adjust'));
      return;
    }

    const taxPc = 1 + parsedTax / 100;
    if (percentageVariation?.direction === 'minus') {
      const newAmount = (100 - (100 - parsedAdjustPercent) / taxPc).toPrecision(6);
      form.setFieldValue('automaticPricing.percentageVariation.amount', newAmount);
    } else {
      const newAmount = ((100 + parsedAdjustPercent) / taxPc - 100).toPrecision(6);
      form.setFieldValue('automaticPricing.percentageVariation.amount', newAmount);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Text size="md">
          {t('price_list_builder.automatic_pricing.percentage_variation.tax.title')}
        </Text>
        <Text size="md" variant="secondary">
          <Trans
            i18nKey={'price_list_builder.automatic_pricing.percentage_variation.tax.description'}
          />{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/shipping-and-tax#9bFOI" />
        </Text>
        <Text size="md" variant="secondary">
          {t('price_list_builder.automatic_pricing.percentage_variation.tax.example', {
            currencySymbol,
          })}
        </Text>
      </div>
      <span className="flex gap-2">
        <Input suffix="%" value={tax} onChange={setTax} inputMode="decimal" short />
        <Button variant="secondary" onClick={adjustPercentageTax}>
          {t('price_list_builder.automatic_pricing.percentage_variation.tax.adjust')}
        </Button>
      </span>
    </div>
  );
}

export function PercentageVariationForm() {
  const form = usePriceListForm();
  const { tax } = useTax();

  const automaticPricing = useStore(form.store, (state) => state.values.automaticPricing);

  if (!automaticPricing?.percentageVariation) {
    return null;
  }

  const showTax =
    tax?.taxes_included &&
    (SHOPIFY_PRICE_LISTS as readonly string[]).includes(automaticPricing.priceListSource);

  return (
    <div className="flex flex-col gap-3">
      <span className="flex gap-2">
        {/* TODO: (styling) remove fixed width */}
        <span className="w-32">
          <form.Field name="automaticPricing.percentageVariation.direction">
            {(field) => (
              <Dropdown
                value={field.state.value ?? ''}
                error={field.state.meta.errors[0]?.message}
                onChange={(value) => field.handleChange(value as 'plus' | 'minus')}
                options={[
                  {
                    label: t(
                      'price_list_builder.automatic_pricing.percentage_variation.direction.minus',
                    ),
                    value: 'minus',
                  },
                  {
                    label: t(
                      'price_list_builder.automatic_pricing.percentage_variation.direction.plus',
                    ),
                    value: 'plus',
                  },
                ]}
              />
            )}
          </form.Field>
        </span>
        <form.Field name="automaticPricing.percentageVariation.amount">
          {(field) => (
            <Input
              suffix="%"
              value={field.state.value}
              error={field.state.meta.errors[0]?.message}
              onChange={field.handleChange}
              inputMode="decimal"
              short
            />
          )}
        </form.Field>
      </span>
      {showTax && <TaxConfiguration />}
    </div>
  );
}
